import React, { useState, useEffect } from 'react';
import { Coupon } from '../types/Coupon';

interface CouponInputProps {
  onSubmit: (coupon: Omit<Coupon, 'id' | 'createdAt' | 'updatedAt' | 'usedCount'>) => void;
  onCancel: () => void;
  editingCoupon?: Coupon;
}

export const CouponInput: React.FC<CouponInputProps> = ({ onSubmit, onCancel, editingCoupon }) => {
  const formatDateForInput = (date: Date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    code: editingCoupon?.code || '',
    name: editingCoupon?.name || '',
    description: editingCoupon?.description || '',
    discountType: (editingCoupon?.discountType || 'percentage') as 'percentage' | 'fixed-amount',
    discountValue: editingCoupon?.discountValue || 0,
    startDate: editingCoupon ? formatDateForInput(editingCoupon.startDate) : '',
    endDate: editingCoupon ? formatDateForInput(editingCoupon.endDate) : '',
    status: editingCoupon?.status || ('draft' as const),
    usageLimit: editingCoupon?.usageLimit ? String(editingCoupon.usageLimit) : '',
    minPurchaseAmount: editingCoupon?.minPurchaseAmount ? String(editingCoupon.minPurchaseAmount) : ''
  });

  useEffect(() => {
    if (editingCoupon) {
      setFormData({
        code: editingCoupon.code,
        name: editingCoupon.name,
        description: editingCoupon.description,
        discountType: editingCoupon.discountType,
        discountValue: editingCoupon.discountValue,
        startDate: formatDateForInput(editingCoupon.startDate),
        endDate: formatDateForInput(editingCoupon.endDate),
        status: editingCoupon.status,
        usageLimit: editingCoupon.usageLimit ? String(editingCoupon.usageLimit) : '',
        minPurchaseAmount: editingCoupon.minPurchaseAmount ? String(editingCoupon.minPurchaseAmount) : ''
      });
    }
  }, [editingCoupon]);

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code }));
    if (errors.code) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.code;
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'クーポンコードは必須です';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'クーポン名は必須です';
    }
    
    if (formData.discountValue <= 0) {
      newErrors.discountValue = '割引額は0より大きい値を入力してください';
    }
    
    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      newErrors.discountValue = '割引率は100%以下である必要があります';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = '開始日は必須です';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = '終了日は必須です';
    }
    
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = '終了日は開始日より後である必要があります';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit({
      ...formData,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : undefined,
      minPurchaseAmount: formData.minPurchaseAmount ? parseFloat(formData.minPurchaseAmount) : undefined
    });
    
    // フォームをリセット
    setFormData({
      code: '',
      name: '',
      description: '',
      discountType: 'percentage',
      discountValue: 0,
      startDate: '',
      endDate: '',
      status: 'draft',
      usageLimit: '',
      minPurchaseAmount: ''
    });
    setErrors({});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'discountValue' ? parseFloat(value) || 0 : value
    }));
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'draft': return '下書き';
      case 'active': return '有効';
      case 'inactive': return '無効';
      case 'expired': return '期限切れ';
      default: return status;
    }
  };

  const formatPreviewDiscount = () => {
    if (formData.discountValue === 0) return '(未入力)';
    if (formData.discountType === 'percentage') {
      return `${formData.discountValue}%割引`;
    } else {
      return `¥${formData.discountValue.toLocaleString()}割引`;
    }
  };

  return (
    <div className="project-input-container">
      <div className="project-input-header">
        <h2>{editingCoupon ? 'クーポン編集' : 'クーポン発行入力'}</h2>
      </div>

      <div className="project-input-content">
        <form onSubmit={handleSubmit} className="project-input-form">
          <div className="form-sections-wrapper">
            <div className="form-section">
              <h3 className="section-title">基本情報</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="code" className="required">クーポンコード</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={errors.code ? 'error' : ''}
                    placeholder="例: SUMMER2024"
                    style={{ flex: 1 }}
                  />
                  <button 
                    type="button" 
                    onClick={generateRandomCode}
                    className="btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ランダム生成
                  </button>
                </div>
                {errors.code && <span className="error-message">{errors.code}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="name" className="required">クーポン名</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="例: 夏のセール20%オフ"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">説明</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="クーポンの詳細説明を入力してください"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-subsections">
              <div className="form-subsection">
                <h3 className="section-title">割引設定</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="discountType">割引タイプ</label>
                    <select
                      id="discountType"
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleChange}
                    >
                      <option value="percentage">割引率（%）</option>
                      <option value="fixed-amount">固定金額（円）</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="discountValue" className="required">
                      {formData.discountType === 'percentage' ? '割引率 (%)' : '割引額 (円)'}
                    </label>
                    <input
                      type="number"
                      id="discountValue"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleChange}
                      className={errors.discountValue ? 'error' : ''}
                      min="0"
                      max={formData.discountType === 'percentage' ? '100' : undefined}
                      step={formData.discountType === 'percentage' ? '1' : '1'}
                    />
                    {errors.discountValue && <span className="error-message">{errors.discountValue}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="minPurchaseAmount">最低購入金額 (円)</label>
                    <input
                      type="number"
                      id="minPurchaseAmount"
                      name="minPurchaseAmount"
                      value={formData.minPurchaseAmount}
                      onChange={handleChange}
                      min="0"
                      placeholder="0（制限なし）"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="usageLimit">使用回数制限</label>
                    <input
                      type="number"
                      id="usageLimit"
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleChange}
                      min="0"
                      placeholder="0（制限なし）"
                    />
                  </div>
                </div>
              </div>

              <div className="form-subsection">
                <h3 className="section-title">有効期間</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="startDate" className="required">開始日</label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className={errors.startDate ? 'error' : ''}
                    />
                    {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="endDate" className="required">終了日</label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className={errors.endDate ? 'error' : ''}
                    />
                    {errors.endDate && <span className="error-message">{errors.endDate}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

            <div className="form-section">
              <h3 className="section-title">ステータス</h3>
              
              <div className="form-group">
                <label htmlFor="status">ステータス</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="draft">下書き</option>
                  <option value="active">有効</option>
                  <option value="inactive">無効</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={onCancel} className="btn-cancel">
                キャンセル
              </button>
              <button type="submit" className="btn-submit">
                {editingCoupon ? '更新' : '作成'}
              </button>
            </div>
          </div>

          <div className="form-preview">
            <h3 className="section-title">プレビュー</h3>
            <div className="preview-card">
              <div className="preview-row">
                <span className="preview-label">クーポンコード:</span>
                <span className="preview-value">{formData.code || '(未入力)'}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">クーポン名:</span>
                <span className="preview-value">{formData.name || '(未入力)'}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">割引内容:</span>
                <span className="preview-value">{formatPreviewDiscount()}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">有効期間:</span>
                <span className="preview-value">
                  {formData.startDate && formData.endDate 
                    ? `${formData.startDate} 〜 ${formData.endDate}` 
                    : '(未入力)'}
                </span>
              </div>
              <div className="preview-row">
                <span className="preview-label">ステータス:</span>
                <span className="preview-value">{getStatusLabel(formData.status)}</span>
              </div>
              {formData.minPurchaseAmount && (
                <div className="preview-row">
                  <span className="preview-label">最低購入金額:</span>
                  <span className="preview-value">¥{parseFloat(formData.minPurchaseAmount).toLocaleString()}</span>
                </div>
              )}
              {formData.usageLimit && (
                <div className="preview-row">
                  <span className="preview-label">使用回数制限:</span>
                  <span className="preview-value">{formData.usageLimit}回</span>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

